import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useLoyaltyPerEntityMutations } from '~/hooks/use-loyalty-per-entity';
import { loyaltyEventTypeSchema, type LoyaltyEventTypeFormValues } from '~/lib/schemas/loyalty-event-type';
import { dayEndIso, dayStartIso, toDayInput } from '~/lib/loyalty-dates';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: LoyaltyEventTypeFormValues | null;
}


/** Edit the points awarded for a loyalty event type (Ανάθεση πόντων). The event name is read-only. */
export function PerEntityEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { update } = useLoyaltyPerEntityMutations();
	const form = useForm<LoyaltyEventTypeFormValues>({
		resolver: zodResolver(loyaltyEventTypeSchema) as Resolver<LoyaltyEventTypeFormValues>,
		defaultValues: initial ?? undefined,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const afrom = watch('afrom');
	const ato = watch('ato');

	useEffect(() => {
		if (open && initial) reset(initial);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			await update.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('loyalty.perEntity.editTitle')}</DialogTitle>
					<DialogDescription>{initial?.nm ?? t('loyalty.perEntity.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('loyalty.perEntity.columns.description')} error={formState.errors.descr?.message}>
						<Textarea {...register('descr')} maxLength={500} rows={2} aria-invalid={Boolean(formState.errors.descr)} autoFocus />
					</FormField>
					<FormField label={t('loyalty.perEntity.columns.points')} error={formState.errors.pnt?.message}>
						<Input
							type="number"
							step={1}
							{...register('pnt', { setValueAs: v => (v === '' || v == null ? undefined : Number(v)) })}
							aria-invalid={Boolean(formState.errors.pnt)}
						/>
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('loyalty.perEntity.columns.activeFrom')} error={formState.errors.afrom?.message}>
							<Input
								type="date"
								required
								value={toDayInput(afrom)}
								onChange={e => setValue('afrom', dayStartIso(e.target.value) ?? '', { shouldDirty: true, shouldValidate: true })}
							/>
						</FormField>
						<FormField label={t('loyalty.perEntity.columns.activeTo')} error={formState.errors.ato?.message}>
							<Input
								type="date"
								value={toDayInput(ato)}
								onChange={e => setValue('ato', dayEndIso(e.target.value), { shouldDirty: true, shouldValidate: true })}
							/>
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={update.isPending}>
							{update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
